import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { dateTime, humanise, num, rupees } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Chips, Modal, Notice, SearchInput, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';

type Filter = 'ALL' | 'LIVE' | 'SOLD' | 'DELETED';

interface PropertyRow {
  id: string;
  title: string;
  priceInr: string | null;
  image: string | null;
  createdAt: string;
  category: { name: string };
  seller: { id: string; displayName: string | null };
  status: string;
  soldOutAt: string | null;
  deleteAt: string | null;
  deletedAt: string | null;
  totalViews: number;
  uniqueMembers: number;
  repeatViews: number;
  guestViews: number;
  enquiries: number;
}

interface HistoryRow {
  id: string;
  action: string;
  createdAt: string;
  after: Record<string, unknown> | null;
  actor: { id: string; displayName: string | null } | null;
}

const TONE: Record<string, BadgeTone> = {
  LIVE: 'green',
  SOLD: 'amber',
  DELETED: 'neutral',
  HOLD: 'neutral',
  EXPIRED: 'neutral',
};

/** "in 3 hours", "overdue 2 min": how long until the sweep deletes it. */
function countdown(deleteAt: string | null): string {
  if (!deleteAt) return '';
  const ms = new Date(deleteAt).getTime() - Date.now();
  const abs = Math.abs(ms);
  const text = abs >= 3_600_000 ? `${Math.floor(abs / 3_600_000)} h ${Math.floor((abs % 3_600_000) / 60_000)} min` : `${Math.max(1, Math.round(abs / 60_000))} min`;
  return ms >= 0 ? `in ${text}` : `overdue ${text}`;
}

/**
 * Properties, with their views, unique members and enquiries, and the
 * sold-out clock: when each was sold, when it will be (or was) deleted.
 */
export function PropertiesPage() {
  const { get, set } = useListParams();
  const status = (get('status') || 'ALL') as Filter;
  const q = get('q');
  const [history, setHistory] = useState<PropertyRow | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'sold' | 'remove'; row: PropertyRow } | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const act = useMutation({
    mutationFn: ({ kind, row }: { kind: 'sold' | 'remove'; row: PropertyRow }) =>
      kind === 'sold'
        ? api.post(`/admin/listings/${row.id}/sold`)
        : api.patch(`/admin/listings/${row.id}/moderate`, { status: 'DELETED', reason: 'Removed from the Properties page' }),
    onSuccess: (_, { kind }) => {
      toast(kind === 'sold' ? 'Marked sold. It will be deleted in 24 hours.' : 'Removed', 'success');
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['admin-properties'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<PropertyRow>> = [
    {
      key: 'property',
      header: 'Property',
      render: (r) => (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {r.image ? (
            <img src={r.image} alt="" style={{ width: 52, height: 40, objectFit: 'cover', borderRadius: 6 }} />
          ) : (
            <div style={{ width: 52, height: 40, borderRadius: 6, background: 'var(--surface-2, #eef2f8)' }} />
          )}
          <div>
            <div className="cell-strong">{r.title}</div>
            <div className="cell-muted">
              {r.category.name} · <Link to={`/users/${r.seller.id}`} onClick={(e) => e.stopPropagation()}>{r.seller.displayName ?? 'Unnamed'}</Link>
            </div>
          </div>
        </div>
      ),
    },
    { key: 'price', header: 'Price', render: (r) => (r.priceInr ? rupees(r.priceInr) : '—') },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge tone={TONE[r.status] ?? 'neutral'}>{r.status === 'SOLD' ? 'Sold out' : humanise(r.status)}</Badge>,
    },
    {
      key: 'views',
      header: 'Views · unique · repeat',
      render: (r) => (
        <span title={`${num(r.guestViews)} by guests or before tracking began`}>
          {num(r.totalViews)} · {num(r.uniqueMembers)} · {num(r.repeatViews)}
        </span>
      ),
    },
    { key: 'enquiries', header: 'Enquiries', render: (r) => num(r.enquiries) },
    {
      key: 'clock',
      header: 'Sold · deletion',
      render: (r) =>
        r.soldOutAt ? (
          <div>
            <div className="cell-muted">Sold {dateTime(r.soldOutAt)}</div>
            {r.deletedAt ? (
              <div className="cell-muted">Deleted {dateTime(r.deletedAt)}</div>
            ) : (
              <div className="cell-strong">
                Deletes {dateTime(r.deleteAt)} <span className="cell-muted">({countdown(r.deleteAt)})</span>
              </div>
            )}
          </div>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <Button size="sm" onClick={() => setHistory(r)}>History</Button>
          {['LIVE', 'HOLD', 'EXPIRED'].includes(r.status) && (
            <Button size="sm" variant="primary" onClick={() => setConfirm({ kind: 'sold', row: r })}>Mark sold</Button>
          )}
          {r.status !== 'DELETED' && (
            <Button size="sm" variant="danger" onClick={() => setConfirm({ kind: 'remove', row: r })}>Remove now</Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="stack">
      <Notice tone="info" title="Sold properties are deleted automatically">
        A property marked sold leaves search at once and is deleted 24 hours later by the server, checked every minute. Its
        photos are removed then; the record, its views and enquiries stay here for reporting.
      </Notice>
      <DataTable<PropertyRow>
        queryKey={['admin-properties', status, q]}
        path="/admin/properties"
        params={{ status: status === 'ALL' ? '' : status, q }}
        columns={columns}
        onRowClick={(r) => setHistory(r)}
        emptyIcon="🏠"
        emptyTitle={status === 'SOLD' ? 'No sold properties waiting for deletion' : 'No properties here'}
        toolbar={
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chips<Filter>
              value={status}
              onChange={(v) => set('status', v === 'ALL' ? '' : v)}
              options={[
                { value: 'ALL', label: 'All' },
                { value: 'LIVE', label: 'Active' },
                { value: 'SOLD', label: 'Sold out' },
                { value: 'DELETED', label: 'Auto-deleted' },
              ]}
            />
            <SearchInput value={q} onChange={(v) => set('q', v)} placeholder="Search by title" />
          </div>
        }
      />

      {history && <HistoryModal row={history} onClose={() => setHistory(null)} />}

      {confirm && (
        <Modal
          title={confirm.kind === 'sold' ? 'Mark this property sold?' : 'Remove this property now?'}
          sub={confirm.row.title}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <Button onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                variant={confirm.kind === 'sold' ? 'primary' : 'danger'}
                loading={act.isPending}
                onClick={() => act.mutate(confirm)}
              >
                {confirm.kind === 'sold' ? 'Mark sold' : 'Remove'}
              </Button>
            </>
          }
        >
          <p>
            {confirm.kind === 'sold'
              ? 'It leaves search now, shows SOLD OUT to anyone with its link, takes no new enquiries, and is deleted automatically in 24 hours. This is recorded against you.'
              : 'It is taken off the app immediately, without waiting for the 24 hours. This is recorded against you.'}
          </p>
        </Modal>
      )}
    </div>
  );
}

function HistoryModal({ row, onClose }: { row: PropertyRow; onClose: () => void }) {
  const history = useQuery({
    queryKey: ['listing-history', row.id],
    queryFn: () => api.get<HistoryRow[]>(`/admin/listings/${row.id}/history`),
  });
  return (
    <Modal title="Property history" sub={row.title} onClose={onClose} wide>
      <div className="kv" style={{ marginBottom: 'var(--s4)' }}>
        <span className="kv__k">Total views</span>
        <span className="kv__v">{num(row.totalViews)}</span>
        <span className="kv__k">Unique members</span>
        <span className="kv__v">{num(row.uniqueMembers)}</span>
        <span className="kv__k">Repeat views</span>
        <span className="kv__v">{num(row.repeatViews)}</span>
        <span className="kv__k">Guest / earlier views</span>
        <span className="kv__v">{num(row.guestViews)}</span>
        <span className="kv__k">Enquiries</span>
        <span className="kv__v">{num(row.enquiries)}</span>
        <span className="kv__k">Sold out</span>
        <span className="kv__v">{dateTime(row.soldOutAt)}</span>
        <span className="kv__k">{row.deletedAt ? 'Deleted' : 'Deletion due'}</span>
        <span className="kv__v">{dateTime(row.deletedAt ?? row.deleteAt)}</span>
      </div>
      {history.isLoading ? (
        <div className="skeleton" style={{ height: 120 }} />
      ) : !history.data?.length ? (
        <p className="cell-muted">Nothing recorded yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th>Who</th>
            </tr>
          </thead>
          <tbody>
            {history.data.map((h) => (
              <tr key={h.id}>
                <td className="cell-muted">{dateTime(h.createdAt)}</td>
                <td className="cell-strong">{humanise(h.action)}</td>
                <td>{h.actor?.displayName ?? (h.action === 'LISTING_AUTO_DELETED' ? 'Automatic' : '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
